// Admin analytics data layer. Every rollup is computed server-side by an
// is_admin()-gated RPC (see migration admin_analytics) so the client never
// pulls whole tables. Results are held in component state only.

import { supabase } from "./supabase";

export type AnalyticsOverview = {
  total_users: number;
  by_role: { student: number; client: number; judge: number; admin: number };
  signups: { today: number; d7: number; d30: number; prev7: number; prev30: number };
  active: { m5: number; m30: number; m60: number; h24: number };
  active_cases: number;
  scholarship_live_cents: number;
};

export async function getAnalyticsOverview(): Promise<AnalyticsOverview | null> {
  const { data, error } = await supabase.rpc("admin_analytics_overview");
  if (error || !data) {
    if (error) console.warn("admin_analytics_overview failed", error.message);
    return null;
  }
  return data as AnalyticsOverview;
}

export type SignupPoint = { bucket: string; role: string; n: number };

export async function getSignupSeries(
  days: number | null,
  bucket: "day" | "week",
): Promise<SignupPoint[]> {
  const { data, error } = await supabase.rpc("admin_analytics_signups", {
    p_days: days,
    p_bucket: bucket,
  });
  if (error || !data) return [];
  return data as SignupPoint[];
}

export type RecentSignup = {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string;
  created_at: string;
  verified: boolean;
};

export async function getRecentSignups(
  limit = 50,
  sinceIso: string | null = null,
): Promise<RecentSignup[]> {
  const { data, error } = await supabase.rpc("admin_analytics_recent_signups", {
    p_limit: limit,
    p_since: sinceIso,
  });
  if (error || !data) return [];
  return data as RecentSignup[];
}

export type AnalyticsFunnel = {
  students_total: number;
  students_registered: number;
  students_submitted: number;
  inquiries_total: number;
  cases_submitted: number;
  cases_live_or_done: number;
};

export async function getAnalyticsFunnel(): Promise<AnalyticsFunnel | null> {
  const { data, error } = await supabase.rpc("admin_analytics_funnel");
  if (error || !data) return null;
  return data as AnalyticsFunnel;
}

/** % change vs the prior period; null when the prior period was empty. */
export function pctChange(current: number, prior: number): number | null {
  if (!prior) return null;
  return Math.round(((current - prior) / prior) * 100);
}
